import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { TrendingUp } from "react-feather";
import colors from "../../../../shared/constants/color";
import {
  headingH5,
  textBase,
  textSM,
  textXS,
} from "../../../../shared/constants";
import { useGetPositionDifficulties } from "../../../../hooks/position/useGetPositionDifficulties";
import {
  useUpdatePositionDifficulty,
  DIFFICULTY_LVL_ID,
} from "../../../../hooks/position/useUpdatePositionDifficulty";
import { useToast } from "../../../../shared/hooks/useToast";
import { PositionDifficultyLevelGuids } from "../../../../constants/parameterGuids";
import { useWiserTheme } from "../../../../hooks/useWiserTheme";

// API'den gelecek kriter tipi – backend DTO ile eşleştirilebilir
export type DifficultyCriterion = {
  /** positionDifficultyInfo satır id (UpdatePositionDifficulty body'deki id) */
  id: string | number;
  name: string;
  order?: number;
  /** Update body'de kullanılır; API verisinde var */
  difficultyId?: string;
};

export type DifficultyLevelValue = "Orta" | "Yüksek";

const POINTS: Record<DifficultyLevelValue, number> = {
  Orta: 1,
  Yüksek: 2,
};

/** Bu sayfada toast'lar 1 sn gösterilir (isteğe göre değiştirilebilir) */
const TOAST_DURATION_MS = 1000;

/** Master difficultyId — bu kriterlerde "Yüksek" gösterilmez (satır `id` değil) */
const CRITERIA_WITHOUT_HIGH_OPTION = new Set([41, 42, 43, 44, 45]);

const CRITERIA_WITHOUT_HIGH_NAMES = new Set(
  [
    "Firma imajı",
    "Yönetici beklentisinin yüksek olması",
    "Kısa ihbar süresi",
    "Eğitim, Okul",
    "Kurum kültürü",
  ].map((s) => s.toLocaleLowerCase("tr-TR")),
);

function isCriterionWithoutHighOption(criterion: {
  difficultyId?: string | number | null;
  name?: string;
}): boolean {
  const did = criterion.difficultyId;
  if (
    did != null &&
    did !== 0 &&
    CRITERIA_WITHOUT_HIGH_OPTION.has(Number(did))
  ) {
    return true;
  }
  const label = criterion.name?.trim();
  if (!label) return false;
  return CRITERIA_WITHOUT_HIGH_NAMES.has(label.toLocaleLowerCase("tr-TR"));
}

function criterionAllowsHighOption(criterion: DifficultyCriterion): boolean {
  return !isCriterionWithoutHighOption({
    difficultyId: criterion.difficultyId,
    name: criterion.name,
  });
}

// Orta: turuncu tema | Yüksek: kırmızı-turuncu tema (unselected / hover / selected)
const OPTION_STYLES: Record<
  DifficultyLevelValue,
  { border: string; bg: string; text: string; hoverBorder: string }
> = {
  Orta: {
    border: colors.neutral.N30,
    bg: colors.yellow.Y50,
    text: colors.yellow.Y500,
    hoverBorder: colors.yellow.Y500,
  },
  Yüksek: {
    border: colors.neutral.N30,
    bg: colors.red.R50,
    text: colors.red.R500,
    hoverBorder: colors.red.R500,
  },
};

type DifficultyLevelProps = {
  /** Pozisyon ID – API GetPositionDifficulties ile kriter listesi çekilir */
  positionId?: string | number;
  /** Dışarıdan kriter listesi (API cevabı). Verilirse hook verisi override edilir */
  criteria?: DifficultyCriterion[];
  /** true ise sadece gösterim, tıklama/update yok */
  readOnly?: boolean;
};

export const DifficultyLevel: React.FC<DifficultyLevelProps> = ({
  positionId,
  criteria: criteriaProp,
  readOnly = false,
}) => {
  const {
    data: positionDifficulties,
    loading,
    refetch: refetchDifficulties,
  } = useGetPositionDifficulties(positionId);
  const { updatePositionDifficulty } = useUpdatePositionDifficulty();
  const { darkModeActive, tokens } = useWiserTheme();
  const { showToast } = useToast();

  // criteria prop > API verisi (positionDifficultyInfo) > mock
  const criteria: DifficultyCriterion[] = useMemo(() => {
    if (criteriaProp && criteriaProp.length > 0) return criteriaProp;
    const list = positionDifficulties?.positionDifficultyInfo;
    if (list?.length) {
      return list.map((item) => ({
        id: item.id,
        difficultyId: item.difficultyId,
        name: item.difficultyName,
      }));
    }
    return [];
  }, [criteriaProp, positionDifficulties]);

  // Seçimler: kriter (row) id -> "Orta" | "Yüksek"
  const [selections, setSelections] = useState<
    Record<string | number, DifficultyLevelValue>
  >({});

  // API'den gelen difficultyLvlId ile başlangıç seçimlerini doldur
  useEffect(() => {
    const list = positionDifficulties?.positionDifficultyInfo;
    if (!list?.length) return;
    const initial: Record<string | number, DifficultyLevelValue> = {};
    list.forEach((item) => {
      const restricted = isCriterionWithoutHighOption({
        difficultyId: item.difficultyId,
        name: item.difficultyName,
      });
      if (item.difficultyLvlId === PositionDifficultyLevelGuids.high && !restricted) {
        initial[item.id] = "Yüksek";
      } else if (item.difficultyLvlId === PositionDifficultyLevelGuids.medium) {
        initial[item.id] = "Orta";
      }
    });
    setSelections(initial);
  }, [positionDifficulties]);

  const handleChange = async (
    criterion: DifficultyCriterion,
    value: DifficultyLevelValue,
  ) => {
    if (readOnly) return;
    const current = selections[criterion.id];
    const isClearing = current === value;

    if (isClearing) {
      setSelections((prev) => {
        const next = { ...prev };
        delete next[criterion.id];
        return next;
      });
      if (!positionId || criterion.difficultyId == null) return;
      const success = await updatePositionDifficulty({
        id: String(criterion.id),
        positionId: String(positionId),
        difficultyId: String(criterion.difficultyId),
        difficultyLvlId: null,
      });
      if (success) {
        showToast(
          "Zorluk derecesi güncellendi",
          "success",
          undefined,
          TOAST_DURATION_MS,
        );
        refetchDifficulties();
      } else {
        showToast(
          "Zorluk derecesi güncellenemedi",
          "error",
          undefined,
          TOAST_DURATION_MS,
        );
        setSelections((prev) => ({ ...prev, [criterion.id]: value }));
      }
      return;
    }

    setSelections((prev) => ({ ...prev, [criterion.id]: value }));

    if (!positionId || criterion.difficultyId == null) return;

    const success = await updatePositionDifficulty({
      id: String(criterion.id),
      positionId: String(positionId),
      difficultyId: String(criterion.difficultyId),
      difficultyLvlId: DIFFICULTY_LVL_ID[value],
    });

    if (success) {
      showToast(
        "Zorluk derecesi güncellendi",
        "success",
        undefined,
        TOAST_DURATION_MS,
      );
      refetchDifficulties();
    } else {
      showToast(
        "Zorluk derecesi güncellenemedi",
        "error",
        undefined,
        TOAST_DURATION_MS,
      );
      setSelections((prev) => {
        const next = { ...prev };
        delete next[criterion.id];
        return next;
      });
    }
  };

  const totalScore = useMemo(() => {
    return Object.values(selections).reduce(
      (sum, level) => sum + POINTS[level],
      0,
    );
  }, [selections]);

  // Sıralı liste (API'den order alanıyla da gelebilir)
  const sortedCriteria = useMemo(
    () => [...criteria].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [criteria],
  );

  if (positionId && loading && !positionDifficulties) {
    return (
      <Box
        sx={{
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  const visibleCriteria = readOnly
    ? sortedCriteria.filter((c) => selections[c.id] !== undefined)
    : sortedCriteria;

  if (readOnly && visibleCriteria.length === 0) {
    return (
      <Box sx={{ pt: 1 }}>
        <Typography
          sx={{
            fontSize: 14,
            color: colors.neutral.N500,
            textAlign: "center",
            py: 3,
          }}
        >
          Herhangi bir zorluk kriteri eklenmemiştir
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 1 }}>
      <Typography
        sx={{
          fontSize: 13,
          color: darkModeActive ? tokens.textSecondary : colors.gray500,
          mb: 2,
        }}
      >
        {readOnly
          ? "Zorluk seviyeleri"
          : "Her kriter için zorluk seviyesi seçin"}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {visibleCriteria.map((criterion, index) => {
          const value = selections[criterion.id];
          const displayOrder = index + 1;

          const optionsToRender = readOnly
            ? value
              ? [value]
              : []
            : criterionAllowsHighOption(criterion)
              ? (["Orta", "Yüksek"] as const)
              : (["Orta"] as const);

          return (
            <Box
              key={criterion.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: "10px 16px",
                backgroundColor: darkModeActive ? "transparent" : colors.neutral.N10,
                border: darkModeActive ? `1px solid ${tokens.border}` : "none",
                borderRadius: "10px",
                "&:hover": {
                  backgroundColor: darkModeActive ? tokens.hoverBg : colors.neutral.N20,
                },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: colors.deepBlue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  ...textSM(600, colors.blue.B300),
                  background: colors.blue.B50,
                }}
              >
                {displayOrder}
              </Box>

              <Typography
                sx={{
                  flex: 1,
                  ...textSM(500, darkModeActive ? tokens.textPrimary : colors.neutral.N900),
                }}
              >
                {criterion.name}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: readOnly ? "flex-end" : "flex-start",
                  gap: 1.5,
                  flexShrink: 0,
                  width: "181px",
                }}
              >
                {(optionsToRender as DifficultyLevelValue[]).map((option) => {
                  const isSelected = value === option;
                  const style = OPTION_STYLES[option];
                  return (
                    <Box
                      key={option}
                      onClick={
                        readOnly
                          ? undefined
                          : () => handleChange(criterion, option)
                      }
                      role={readOnly ? undefined : "button"}
                      tabIndex={readOnly ? undefined : 0}
                      onKeyDown={
                        readOnly
                          ? undefined
                          : (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleChange(criterion, option);
                              }
                            }
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1.5,
                        border: "1px solid",
                        backgroundColor: isSelected
                          ? style.bg
                          : darkModeActive
                            ? tokens.inputBg
                            : "#fff",
                        borderColor: isSelected ? "transparent" : style.border,
                        color: isSelected
                          ? style.text
                          : darkModeActive
                            ? tokens.textSecondary
                            : colors.gray600,
                        cursor: readOnly ? "default" : "pointer",
                        pointerEvents: readOnly ? "none" : "auto",
                        transition:
                          "border-color 0.15s, background-color 0.15s, color 0.15s",
                        "&:hover": readOnly
                          ? {}
                          : {
                              borderColor: style.hoverBorder,
                              backgroundColor: isSelected
                                ? style.bg
                                : darkModeActive
                                  ? tokens.inputBg
                                  : "#fff",
                            },
                        "&:focus-visible": readOnly
                          ? {}
                          : {
                              outline: "2px solid",
                              outlineColor: style.hoverBorder,
                              outlineOffset: 2,
                            },
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          border: "2px solid",
                          borderColor: isSelected ? style.text : colors.gray400,
                          backgroundColor: "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && (
                          <CheckIcon
                            sx={{
                              color: style.text,
                              transform: "scale(0.45)",
                              transformOrigin: "center",
                              filter: "drop-shadow(0 0 0.8px currentColor)",
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "inherit",
                        }}
                      >
                        {option}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Toplam Puanlama */}
      <Box
        sx={{
          mt: 3,
          p: 2.5,
          backgroundColor: colors.neutral.N20,
          borderRadius: "14px",
          border: `2px solid ${colors.neutral.N30}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: colors.blue.B300,
              borderRadius: "50%",
            }}
          >
            <TrendingUp size={24} color="#fff" />
          </Box>
          <Box>
            <Typography sx={{ ...textSM(600, colors.neutral.N900) }}>
              Toplam Puanlama
            </Typography>
            <Typography sx={{ ...textXS(500, colors.neutral.N300), mt: 0.5 }}>
              Orta: 1 puan • Yüksek: 2 puan
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ ...textSM(400, colors.neutral.N300) }}>
            Mevcut Puan
          </Typography>
          <Typography
            sx={{
              ...headingH5(500, colors.neutral.N800),
              fontSize: "30px",
              mt: 1,
            }}
          >
            {totalScore}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
